import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
import { motion } from 'framer-motion';
import { Award, Zap, Clock, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export const About = () => {
  const { t, language } = useLanguage();

  const values = [
    { icon: Award, title: t('title', translations.aboutPage.values.professionalism), description: t('description', translations.aboutPage.values.professionalism) },
    { icon: Zap, title: t('title', translations.aboutPage.values.flexibility), description: t('description', translations.aboutPage.values.flexibility) },
    { icon: Clock, title: t('title', translations.aboutPage.values.experience), description: t('description', translations.aboutPage.values.experience) },
  ];

  return (
    <div data-testid="about-page" className="bg-[#0f1419]">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28" data-testid="about-header">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1646227655718-dd721b681d91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwZW5naW5lZXIlMjB0ZWNobmljaWFuJTIwaGVsbWV0JTIwc2FmZXR5fGVufDB8fHx8MTc3MzA3NDUyNnww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419] via-[#0f1419]/95 to-[#0f1419]" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">ABOUT US</p>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="about-title">
              {t('title', translations.aboutPage)}
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl">
              {t('subtitle', translations.aboutPage)}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: 'Nordic', label: language === 'en' ? 'Region' : 'Region' },
              { value: '500+', label: language === 'en' ? 'Projects' : 'Projekt' },
              { value: '100+', label: language === 'en' ? 'Clients' : 'Kunder' },
              { value: '50+', label: language === 'en' ? 'Team' : 'Team' },
            ].map((stat, i) => (
              <div key={i} className="text-center" data-testid={`stat-${i}`}>
                <p className="text-3xl lg:text-4xl font-bold text-white">{stat.value}</p>
                <p className="text-blue-100 text-xs font-mono tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 lg:py-28 border-t border-gray-800/50" data-testid="about-story-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeIn} className="order-2 lg:order-1">
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1621999625968-1ff64ab75fb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwZW5naW5lZXIlMjB0ZWNobmljaWFuJTIwaGVsbWV0JTIwc2FmZXR5fGVufDB8fHx8MTc3MzA3NDUyNnww&ixlib=rb-4.1.0&q=85"
                  alt="Industrial operations"
                  className="w-full h-full object-cover"
                  data-testid="about-image"
                />
              </div>
            </motion.div>

            <motion.div {...fadeIn} className="order-1 lg:order-2">
              <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">
                {t('title', translations.aboutPage.story).toUpperCase()}
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6" data-testid="story-title">
                Natcas Group
              </h2>
              <p className="text-gray-400 text-lg leading-relaxed mb-8" data-testid="story-content">
                {t('content', translations.aboutPage.story)}
              </p>
              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 text-xs font-mono">
                  Nordic Region
                </span>
                <span className="px-4 py-2 bg-gray-800 border border-gray-700 text-gray-300 text-xs font-mono">
                  Technical Expertise
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Image Break */}
      <section className="relative h-[40vh]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1769144256227-5185141c3aca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwyfHxjb250YWluZXIlMjBwb3J0JTIwY3JhbmVzJTIwbG9naXN0aWNzJTIwYWVyaWFsJTIwdmlld3xlbnwwfHx8fDE3NzMwNzQ1MTJ8MA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-[#0f1419]/70" />
      </section>

      {/* Values */}
      <section className="py-20 lg:py-28 bg-[#0a0d10] border-t border-gray-800/50" data-testid="about-values-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeIn} className="text-center mb-16">
            <p className="text-blue-400 text-sm font-mono mb-4 tracking-wider">WHAT DRIVES US</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-white" data-testid="values-title">
              {t('title', translations.aboutPage.values)}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, i) => (
              <motion.div
                key={i}
                {...fadeIn}
                className="group p-8 bg-[#0f1419] border border-gray-800 hover:border-blue-600/50 transition-all"
                data-testid={`value-${i}`}
              >
                <div className="w-14 h-14 bg-gray-800 group-hover:bg-blue-600 transition-colors flex items-center justify-center mb-6">
                  <value.icon className="w-6 h-6 text-blue-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div {...fadeIn}>
            <p className="text-blue-400 text-sm font-mono mb-6 tracking-wider">LET'S CONNECT</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Work Together?
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how Natcas Group can support your industrial operations.
            </p>
            <Button
              asChild
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-none px-10 h-14 text-sm font-semibold group"
              data-testid="about-cta"
            >
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
